/**
 * NIVARO — product page behaviour (loaded by sections/product-information.liquid).
 *
 * - Size assistant dialog ([data-nv-open-size-assistant] opens it).
 * - Keeps NIVARO server-rendered PDP blocks ([data-nv-sync]) in sync when the
 *   shopper picks another variant, using the HTML Horizon's variant picker
 *   already fetched (ProductSelectEvent promise → detail.html).
 * - Restock alert toggle, sticky-bar "Avisarme cuando vuelva" scroll.
 * - Mobile sticky CTA visibility (always visible while the main buy buttons are
 *   off screen, hidden near the footer).
 * - Gallery expand button and "Cómo queda puesto" thumbnails open the zoom dialog.
 */

const PRODUCT_SELECT = 'shopify:product:select';
const SIZE_ORDER = ['XS', 'S', 'M', 'L', 'XL', 'XXL', 'XXXL'];
const SIZE_PATTERN = /(talle|talla|size)/i;

/* ------------------------------------------------------------------ helpers */
const $ = (selector, root = document) => root.querySelector(selector);
const $$ = (selector, root = document) => Array.from(root.querySelectorAll(selector));

function mainVariantPicker() {
  return (
    $('.nv-pdp-section variant-picker[data-template-product-match="true"]') ||
    $('.nv-pdp-section variant-picker') ||
    null
  );
}

/** Size values offered by the main product, read from Horizon's variant picker. */
function productSizes() {
  const picker = mainVariantPicker();
  if (!picker) return { values: [], inputs: [] };
  const inputs = $$('input[type="radio"]', picker).filter((input) =>
    SIZE_PATTERN.test(input.dataset.optionName || input.name || '')
  );
  if (inputs.length) return { values: inputs.map((i) => i.value.trim().toUpperCase()), inputs };
  const single = $('[data-testid="variant-option-single-value"]', picker);
  const singleLabel = single?.closest('.variant-option')?.textContent || '';
  if (single && SIZE_PATTERN.test(singleLabel)) return { values: [single.textContent.trim().toUpperCase()], inputs: [] };
  return { values: [], inputs: [] };
}

/* ------------------------------------------------------------ size assistant */
/**
 * Size recommendation heuristic — deliberately simple and transparent:
 *
 * 1. Base size from weight, using reference bands for a straight-cut tee:
 *      Hombre: S ≤ 64 kg · M 65–74 · L 75–84 · XL 85–95 · XXL > 95
 *      Mujer:  the same bands shifted 8 kg lower (S ≤ 56 · M 57–66 · L 67–76 · XL 77–87 · XXL > 87),
 *              because the tees are cut on a men's/unisex block.
 * 2. Height correction for body length: from 188 cm (hombre) / 178 cm (mujer)
 *    we go up one size so the tee isn't short.
 * 3. Clamp to the sizes present in the size table.
 * 4. Oversize products: we suggest the same size (the garment is already cut wide)
 *    and mention the next size up for an even looser look.
 * It's a suggestion, not a promise — the table and WhatsApp are there to confirm.
 */
function recommendSize({ gender, height, weight }, tableSizes) {
  const shift = gender === 'mujer' ? 8 : 0;
  const bands = [64, 74, 84, 95].map((limit) => limit - shift);
  let index = bands.findIndex((limit) => weight <= limit);
  if (index === -1) index = 4; // XXL
  const sizes = ['S', 'M', 'L', 'XL', 'XXL'];

  let heightBump = false;
  const tall = gender === 'mujer' ? 178 : 188;
  if (height >= tall && index < sizes.length - 1) {
    index += 1;
    heightBump = true;
  }

  let size = sizes[index];
  const available = tableSizes.length ? tableSizes : sizes;
  if (!available.includes(size)) {
    // closest size present in the table
    const target = SIZE_ORDER.indexOf(size);
    size = available
      .slice()
      .sort((a, b) => Math.abs(SIZE_ORDER.indexOf(a) - target) - Math.abs(SIZE_ORDER.indexOf(b) - target))[0];
  }
  const order = available.slice().sort((a, b) => SIZE_ORDER.indexOf(a) - SIZE_ORDER.indexOf(b));
  const next = order[order.indexOf(size) + 1] || null;
  return { size, next, heightBump };
}

function setupSizeAssistant(dialog) {
  if (dialog.dataset.nvReady) return;
  dialog.dataset.nvReady = 'true';

  const form = $('[data-nv-sa-form]', dialog);
  const result = $('[data-nv-sa-result]', dialog);
  const error = $('[data-nv-sa-error]', dialog);
  const sizeEl = $('[data-nv-sa-size]', dialog);
  const textEl = $('[data-nv-sa-text]', dialog);
  const selectBtn = $('[data-nv-sa-select]', dialog);
  const whatsapp = $('[data-nv-sa-whatsapp]', dialog);
  const fit = dialog.dataset.fit === 'oversize' ? 'oversize' : 'regular';
  const productTitle = dialog.dataset.productTitle || 'la remera';
  const tableRows = $$('[data-nv-sa-row]', dialog);
  const tableSizes = tableRows.map((row) => row.dataset.nvSaRow);

  $('[data-nv-sa-close]', dialog)?.addEventListener('click', () => dialog.close());
  dialog.addEventListener('click', (event) => {
    if (event.target === dialog) dialog.close(); // backdrop click
  });

  form?.addEventListener('submit', (event) => {
    event.preventDefault();
    const height = Number($('[data-nv-sa-height]', form)?.value);
    const weight = Number($('[data-nv-sa-weight]', form)?.value);
    const gender = $('input[type="radio"]:checked', form)?.value === 'mujer' ? 'mujer' : 'hombre';

    const valid = height >= 140 && height <= 210 && weight >= 40 && weight <= 160;
    if (error) error.hidden = valid;
    if (!valid) return;

    const { size, next, heightBump } = recommendSize({ gender, height, weight }, tableSizes);

    if (sizeEl) sizeEl.textContent = size;
    let copy =
      fit === 'oversize'
        ? 'Te va a quedar amplio, como está pensada la prenda.' +
          (next ? ` Si buscás un calce todavía más suelto, podés ir por el ${next}.` : '')
        : 'Te va a quedar cómodo, con un fit moderno.';
    if (heightBump) copy += ' Subimos un talle por tu altura, para que el largo te quede bien.';

    const { values } = productSizes();
    if (values.length && !values.includes(size)) {
      copy += ` Este modelo no viene en ${size}: consultanos por WhatsApp y te ayudamos.`;
    }
    if (textEl) textEl.textContent = copy;

    tableRows.forEach((row) => row.classList.toggle('is-recommended', row.dataset.nvSaRow === size));

    if (selectBtn) {
      const { inputs } = productSizes();
      const match = inputs.find((input) => input.value.trim().toUpperCase() === size);
      selectBtn.hidden = !match || match.checked;
      selectBtn.textContent = `Elegir talle ${size}`;
      selectBtn.dataset.size = size;
    }

    if (whatsapp?.dataset.waNumber) {
      const message =
        `Hola! Estoy viendo la remera ${productTitle} en NIVARO. ` +
        `Mido ${height} cm y peso ${weight} kg (${gender === 'mujer' ? 'mujer' : 'hombre'}). ` +
        `El asistente me sugirió talle ${size} (calce ${fit}). ¿Me confirman?`;
      whatsapp.href = `https://wa.me/${whatsapp.dataset.waNumber}?text=${encodeURIComponent(message)}`;
    }

    if (result) {
      result.hidden = false;
      result.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    }
  });

  selectBtn?.addEventListener('click', () => {
    const size = selectBtn.dataset.size;
    const match = productSizes().inputs.find((input) => input.value.trim().toUpperCase() === size);
    if (!match) return;
    dialog.close();
    if (!match.checked) match.click();
    match.closest('variant-picker')?.scrollIntoView({ block: 'center', behavior: 'smooth' });
  });
}

function openSizeAssistant(trigger) {
  const id = trigger.getAttribute('data-nv-open-size-assistant');
  const dialog =
    (id && document.getElementById(id)) || $('dialog[data-nv-size-assistant]');
  if (!(dialog instanceof HTMLDialogElement)) return;
  setupSizeAssistant(dialog);
  dialog.showModal();
  $('[data-nv-sa-height]', dialog)?.focus({ preventScroll: true });
}

/* ------------------------------------------------------------- click router */
document.addEventListener('click', (event) => {
  const target = event.target instanceof Element ? event.target : null;
  if (!target) return;

  const sizeTrigger = target.closest('[data-nv-open-size-assistant]');
  if (sizeTrigger) {
    event.preventDefault();
    openSizeAssistant(sizeTrigger);
    return;
  }

  const restockToggle = target.closest('[data-nv-restock-toggle]');
  if (restockToggle) {
    const panel = document.getElementById(restockToggle.getAttribute('aria-controls') || '');
    if (!panel) return;
    const open = restockToggle.getAttribute('aria-expanded') !== 'true';
    restockToggle.setAttribute('aria-expanded', String(open));
    panel.hidden = !open;
    if (open) $('input[type="email"]', panel)?.focus();
    return;
  }

  const scrollRestock = target.closest('[data-nv-scroll-restock]');
  if (scrollRestock) {
    const slot = $('.nv-pdp-section .nv-restock');
    if (!slot) return;
    event.preventDefault();
    slot.scrollIntoView({ block: 'center', behavior: 'smooth' });
    const toggle = $('[data-nv-restock-toggle]', slot);
    if (toggle && toggle.getAttribute('aria-expanded') !== 'true') toggle.click();
    else $('input[type="email"], [data-nv-restock-success]', slot)?.focus({ preventScroll: true });
    return;
  }

  const expand = target.closest('[data-nv-gallery-expand]');
  if (expand) {
    const gallery = expand.closest('media-gallery');
    if (gallery && typeof gallery.zoom === 'function') {
      gallery.zoom(gallery.slideshow?.current ?? 0, event);
    }
    return;
  }

  const zoomMedia = target.closest('[data-nv-zoom-media]');
  if (zoomMedia) {
    const gallery = $('.nv-pdp-section media-gallery');
    const items = gallery ? $$('.dialog-zoomed-gallery > li', gallery) : [];
    const index = items.findIndex((li) => li.id.startsWith(`product-${zoomMedia.dataset.nvZoomMedia}-`));
    if (gallery && index >= 0 && typeof gallery.zoom === 'function') gallery.zoom(index, event);
  }
});

/* ------------------------------------------------- variant change → resync */
document.addEventListener(PRODUCT_SELECT, (event) => {
  const source = event.target instanceof Element ? event.target : null;
  if (!source || source.closest('product-card, quick-add-dialog, .quick-add-modal')) return;
  const section = source.closest('.nv-pdp-section');
  if (!section || !event.promise) return;

  event.promise
    .then(({ detail }) => {
      const html = detail?.html;
      if (!html) return;
      $$('[data-nv-sync]', section).forEach((current) => {
        const key = current.getAttribute('data-nv-sync');
        const fresh = html.querySelector(`[data-nv-sync="${CSS.escape(key)}"]`);
        if (!fresh) return;
        const typedEmail = $('input[type="email"]', current)?.value;
        const wasOpen = $('[data-nv-restock-toggle]', current)?.getAttribute('aria-expanded') === 'true';
        current.replaceWith(document.importNode(fresh, true));
        const next = $(`[data-nv-sync="${CSS.escape(key)}"]`, section);
        if (next && wasOpen) $('[data-nv-restock-toggle]', next)?.click();
        if (next && typedEmail) {
          const email = $('input[type="email"]', next);
          if (email) email.value = typedEmail;
        }
      });
    })
    .catch(() => {});
});

/* ----------------------------------------------- sticky CTA (mobile + sold out) */
function setupSticky() {
  const sticky = $('sticky-add-to-cart.nv-sticky');
  const bar = sticky ? $('.sticky-add-to-cart__bar', sticky) : null;
  const buyButtons = $('.nv-pdp-section .buy-buttons-block');
  if (!sticky || !bar || !buyButtons) return;

  const footer = $('footer') || $('[class*="footer-group"]');
  let buyVisible = true;
  let footerVisible = false;

  const update = () => {
    const show = !buyVisible && !footerVisible;
    // The bar is re-rendered (morphed) on variant change, so look it up each time.
    const currentBar = $('.sticky-add-to-cart__bar', sticky);
    currentBar?.setAttribute('data-nv-show', String(show));
    document.body.classList.toggle('nv-sticky-on', show);
  };

  new IntersectionObserver(([entry]) => {
    buyVisible = Boolean(entry?.isIntersecting);
    update();
  }).observe(buyButtons);

  if (footer) {
    new IntersectionObserver(
      ([entry]) => {
        footerVisible = Boolean(entry?.isIntersecting);
        update();
      },
      { rootMargin: '0px 0px 0px 0px' }
    ).observe(footer);
  }

  new MutationObserver(update).observe(sticky, { childList: true });
}

/* ------------------------------------------- restock success after contact post */
function revealRestockSuccess() {
  const success = $('[data-nv-restock-success]');
  if (!success) return;
  success.scrollIntoView({ block: 'center' });
  success.focus({ preventScroll: true });
}

function init() {
  setupSticky();
  revealRestockSuccess();
}

if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
else init();
